import React from 'react';
import { i18n } from 'i18n';
import ProjectAuthorise from 'modules/project/ProjectAuthorise';
import { ProjectModule } from 'providers/api';
import ModelDrawer from '../../ModelDrawer';
import { navigateToScene, SceneProps } from '../scene-utils';
import { Model } from '../../hooks/useModelState';
import WearMonitoringPanelPage from '../../WearMonitoringPanelPage';
import InspectionPointPanelPage from '../../InspectionPointPanelPage';
import WearDataEntryPanelPage from '../../WearDataEntryPage/WearDataEntryPanelPage';
import WearDataUpdatePanelPage from '../../WearDataUpdatePanelPage/WearDataUpdatePanelPage';

interface WearMonitoringSideDrawerProps {
  props: SceneProps;
  model: Model;
}

const WearMonitoringSideDrawer = ({ props, model }: WearMonitoringSideDrawerProps) => {
  const { drawerOpen } = props.context.state;
  const { setDrawerOpen } = props.context.actions;
  const [currentView, setCurrentView] = React.useState<'list' | 'inspection-point' | 'new-wear-data' | 'edit-wear-data'>('list');
  const [selectedInspectionPointId, setSelectedInspectionPointId] = React.useState<string | null>(null);
  const [selectedWearDataId, setSelectedWearDataId] = React.useState<string | null>(null);

  React.useEffect(() => {
    if (!drawerOpen) {
      setDrawerOpen(true);
    }
  }, [drawerOpen, setDrawerOpen]);

  const handleInspectionPointSelect = (inspectionPointId: string) => {
    setSelectedInspectionPointId(inspectionPointId);
    setCurrentView('inspection-point');
  };

  const handleBackToList = () => {
    setCurrentView('list');
    setSelectedInspectionPointId(null);
    setSelectedWearDataId(null);
  };

  const handleBackToInspectionPoint = () => {
    setCurrentView('inspection-point');
    setSelectedWearDataId(null);
  };

  const handleBackToStopes = () => {
    navigateToScene('DEFAULT', { model, context: props.context });
  };

  const handleNewWearData = () => {
    setCurrentView('new-wear-data');
  };

  const handleEditWearData = (wearDataId: string) => {
    setSelectedWearDataId(wearDataId);
    setCurrentView('edit-wear-data');
  };

  if (currentView === 'inspection-point' && selectedInspectionPointId) {
    return (
      <ProjectAuthorise module={ProjectModule.WearMonitoring}>
        <ModelDrawer
          backNavText={i18n._('Inspection Points')}
          isOpen={drawerOpen}
          setIsOpen={setDrawerOpen}
          onBack={handleBackToList}
        >
          <InspectionPointPanelPage
            wearDataEntryPath=""
            wearDataUpdatePath=""
            unitSystemPreference={model.project.unitSystemPreference}
            inspectionPointId={selectedInspectionPointId}
            projectId={model.project.entityId}
            onNewWearData={handleNewWearData}
            onEditWearData={handleEditWearData}
          />
        </ModelDrawer>
      </ProjectAuthorise>
    );
  }

  if (currentView === 'new-wear-data' && selectedInspectionPointId) {
    return (
      <ProjectAuthorise module={ProjectModule.WearMonitoring}>
        <ModelDrawer
          backNavText={i18n._('Inspection Point')}
          isOpen={drawerOpen}
          setIsOpen={setDrawerOpen}
          onBack={handleBackToInspectionPoint}
        >
          <WearDataEntryPanelPage
            unitSystemPreference={model.project.unitSystemPreference}
            successPath=""
            spools={Object.values(model.spools)}
            inspectionPointId={selectedInspectionPointId}
            projectId={model.project.entityId}
          />
        </ModelDrawer>
      </ProjectAuthorise>
    );
  }

  if (currentView === 'edit-wear-data' && selectedInspectionPointId && selectedWearDataId) {
    return (
      <ProjectAuthorise module={ProjectModule.WearMonitoring}>
        <ModelDrawer
          backNavText={i18n._('Inspection Point')}
          isOpen={drawerOpen}
          setIsOpen={setDrawerOpen}
          onBack={handleBackToInspectionPoint}
        >
          <WearDataUpdatePanelPage
            unitSystemPreference={model.project.unitSystemPreference}
            successPath=""
            spools={Object.values(model.spools)}
            inspectionPointId={selectedInspectionPointId}
            projectId={model.project.entityId}
            wearDataId={selectedWearDataId}
          />
        </ModelDrawer>
      </ProjectAuthorise>
    );
  }

  return (
    <ProjectAuthorise module={ProjectModule.WearMonitoring}>
      <ModelDrawer
        backNavText={i18n._('Stopes')}
        isOpen={drawerOpen}
        setIsOpen={setDrawerOpen}
        onBack={handleBackToStopes}
      >
        <WearMonitoringPanelPage
          model={model}
          wearDataViewPath=""
          onInspectionPointSelect={handleInspectionPointSelect}
        />
      </ModelDrawer>
    </ProjectAuthorise>
  );
};

const sideDrawerContent = (
  props: SceneProps,
  model: Model,
): JSX.Element => <WearMonitoringSideDrawer props={props} model={model} />;

export default sideDrawerContent;
